import React from 'react';
import { useToasts } from 'react-toast-notifications';

/**
 * Hook to show toasts. It's a wrapper around the react-toast-notifications library.
 * This approach is useful to reduce the boilerplate code when showing toasts.
 */
export const useToastHelper = () => {
    const { addToast } = useToasts();

    /**
     * Show a success toast.
     *
     * @param {React.ReactNode} message Message to be shown in the toast
     * @param {Object} [options] Options to customize the toast
     */
    const addSuccessToast = (message, options) => {
        addToast(message, {
            appearance: 'success',
            autoDismiss: true,
            ...options
        });
    };

    /**
     * Show an error toast.
     *
     * @param {React.ReactNode} message Message to be shown in the toast
     * @param {Object} [options] Options to customize the toast
     */
    const addErrorToast = (message, options) => {
        addToast(message, {
            appearance: 'error',
            autoDismiss: true,
            ...options
        });
    };

    /**
     * Show an info toast.
     *
     * @param {React.ReactNode} message Message to be shown in the toast
     * @param {Object} [options] Options to customize the toast
     */
    const addInfoToast = (message, options) => {
        addToast(message, {
            appearance: 'info',
            autoDismiss: true,
            ...options
        });
    };

    /**
     * Show a warning toast.
     *
     * @param {React.ReactNode} message Message to be shown in the toast
     * @param {Object} [options] Options to customize the toast
     */
    const addWarningToast = (message, options) => {
        addToast(message, {
            appearance: 'warning',
            autoDismiss: true,
            ...options
        });
    };

    return {
        addSuccessToast,
        addErrorToast,
        addInfoToast,
        addWarningToast
    };
};

/**
 * Higher-order component (HOC) that injects a `toastHelper` object as a prop
 * into the wrapped component. The `toastHelper` prop provides methods for
 * displaying toast notifications within the application.
 *
 * @function
 * @param {React.ComponentType} Component - The React component to be wrapped by the HOC.
 * @returns {React.ComponentType} A new component that passes down all original props
 *                                along with the `toastHelper` prop.
 */
export const withToastHelper = Component =>
    /**
     * A higher-order component (HOC) that enhances the provided component by injecting a `toastHelper` prop.
     *
     * This HOC uses `useToastHelper` to obtain a helper object for displaying toast notifications
     * and passes it as an additional prop named `toastHelper` to the wrapped component.
     *
     * @param {Object} props - The props passed to the wrapped component.
     * @returns {JSX.Element} The enhanced component with the added `toastHelper` prop.
     */
    props => {
        const toastHelper = useToastHelper();
        return <Component {...props} toastHelper={toastHelper} />;
    };
